export type ReportType = "IA" | "RA";

export interface RuleBodyFields {
  types: ReportType[];
  location: string;
  scanFor: string;
  feedback: string;
}

const ALL_TYPES: ReportType[] = ["IA", "RA"];
const REPORT_TYPE_LABEL = "Report type:";
const LOCATION_LABEL = "Location:";
const SCAN_LABEL = "What to scan for:";
const FEEDBACK_LABEL = "Feedback:";

// Serialize the structured editor fields into the single `instructions` blob
// the backend stores. Format is fixed labeled sections so parseRuleBody can
// recover the fields, and it reads naturally when handed to the LLM.
export const serializeRuleBody = (fields: RuleBodyFields): string => {
  const types = ALL_TYPES.filter((t) => fields.types.includes(t));
  return [
    `${REPORT_TYPE_LABEL} ${types.join(", ")}`,
    `${LOCATION_LABEL} ${fields.location}`.trimEnd(),
    "",
    SCAN_LABEL,
    fields.scanFor,
    "",
    FEEDBACK_LABEL,
    fields.feedback,
  ].join("\n");
};

// Find `label` only where it begins a line (matches the markers we wrote,
// not the same substring occurring inside a user's scan/feedback text).
const lineAnchoredIndexOf = (text: string, label: string, from = 0): number => {
  let idx = text.indexOf(label, from);
  while (idx !== -1) {
    if (idx === 0 || text[idx - 1] === "\n") return idx;
    idx = text.indexOf(label, idx + label.length);
  }
  return idx;
};

const parseLabeledLine = (header: string, label: string): string => {
  const line = header.split("\n").find((l) => l.trimStart().startsWith(label));
  return line ? line.slice(line.indexOf(label) + label.length).trim() : "";
};

const parseTypes = (header: string): ReportType[] => {
  const raw = parseLabeledLine(header, REPORT_TYPE_LABEL);
  const found = ALL_TYPES.filter((t) =>
    raw
      .split(",")
      .map((s) => s.trim())
      .includes(t),
  );
  return found.length > 0 ? found : [...ALL_TYPES];
};

// Recover the fields. Tolerant: a blob that doesn't contain our scan marker
// (e.g. a legacy rule authored before this UI) is shown as scan-for text with
// report type defaulting to both.
export const parseRuleBody = (blob: string): RuleBodyFields => {
  const text = blob ?? "";
  const scanIdx = lineAnchoredIndexOf(text, SCAN_LABEL);
  if (scanIdx === -1) {
    return { types: [...ALL_TYPES], location: "", scanFor: text.trim(), feedback: "" };
  }
  const header = text.slice(0, scanIdx);
  const feedbackIdx = lineAnchoredIndexOf(text, FEEDBACK_LABEL, scanIdx);
  const scanFor = (
    feedbackIdx === -1 ? text.slice(scanIdx + SCAN_LABEL.length) : text.slice(scanIdx + SCAN_LABEL.length, feedbackIdx)
  ).trim();
  const feedback = feedbackIdx === -1 ? "" : text.slice(feedbackIdx + FEEDBACK_LABEL.length).trim();
  return { types: parseTypes(header), location: parseLabeledLine(header, LOCATION_LABEL), scanFor, feedback };
};
